// src/pages/Leads.js
import React, { useEffect, useLayoutEffect, useState, useCallback, useMemo, useRef } from "react";

import {
  MapContainer,
  TileLayer,
  Marker,
  Tooltip as LeafletTooltip,
  Popup,
  useMapEvents,
  useMap,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster";
import "leaflet.markercluster/dist/MarkerCluster.css";
import axios from "axios";
import API from "../utils/api";
import api from "../utils/api";
import {
  Box,
  Typography,
  Button,
  Divider,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Card,
  CardContent,
  IconButton,
  useMediaQuery,
  useTheme,
  Drawer,
  CircularProgress,
  Chip,
  Autocomplete,
  TextField,
  Stack,
  Menu,
  MenuItem,
  Popover,
  Skeleton,
  FormControl,
  InputLabel,
  Select,
  Tooltip as MuiTooltip,
} from "@mui/material";
import useLandscape from "../hooks/useLandscape";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import ClearIcon from "@mui/icons-material/Clear";
import CloseIcon from "@mui/icons-material/Close";
import NavigationIcon from "@mui/icons-material/Navigation";
import CropFreeIcon from "@mui/icons-material/CropFree";
import MapsHomeWorkIcon from "@mui/icons-material/MapsHomeWork";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import FilterListIcon from "@mui/icons-material/FilterList";
import TuneIcon from "@mui/icons-material/Tune";
import MapIcon from "@mui/icons-material/Map";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import { toast } from "react-toastify";
import { tSuccess, tError } from "../utils/toast";
import SaveRouteModal from "../components/SaveRouteModal";
import { useNavigate, useSearchParams } from "react-router-dom";
import useAuth from "../hooks/useAuth";
import ConfirmDialog from "../components/ConfirmDialog";
import { subscribeLeadUpdates, emitLeadUpdate } from "../utils/leadSync";
import STATUS_COLORS from "../utils/statusColors";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import LeadsMobileMapView from "./LeadsMobileMapView";
import MapViewportFetcher from "../components/leads/MapViewportFetcher";
import MapHqFallback from "../components/leads/MapHqFallback";
import MapLastViewPersistence from "../components/leads/MapLastViewPersistence";
import { isLeadsViewportMapFetchEnabled } from "../utils/leadsMapViewportFetch";
import { isLeadsMapClusteringEnabled, isLeadsMapMobileClusteringEnabled, USER_LOCATION_MIN_ZOOM_DESKTOP, USER_LOCATION_LOCATE_ZOOM, USER_LOCATION_PULSE_ZOOM_MIN, USER_LOCATION_PULSE_ZOOM_MAX, USER_LOCATION_ACCURACY_PATH_OPTIONS } from "../utils/leadsMapClustering";
import { createUserLocationIcon } from "../utils/userLocationMarker";
import UserLocationMapLayers from "../components/leads/UserLocationMapLayers";
import ClusteredLeadMarkers from "../components/leads/ClusteredLeadMarkers";
import { getCachedMarkerIcon } from "../utils/leadsMapPinIcons";
import {
  fetchLeadDateSourcePins,
  fetchMapPins,
} from "../utils/leadsMapData";
import {
  LEADS_MAP_CONTINENTAL_US,
  leadHasMapCoordinates,
  parseCompanyMapDefault,
  readLeadsMapLastView,
} from "../utils/leadsMapInitialView";
import {
  LEAD_STATUS_DEFINITIONS,
  getLeadStatusChipStyle,
} from "../constants/leadStatuses";
import LeadStatusIcon from "../components/LeadStatusIcon";

const DESKTOP_USER_LOCATION_ICON = createUserLocationIcon({ pulsing: false });
const DESKTOP_USER_LOCATION_ICON_PULSE = createUserLocationIcon({
  pulsing: true,
  pulseStrength: "desktop",
});

// ---------------- DESKTOP DRAWER CONSTANTS ----------------

const getDesktopStatusChipStyle = (status) => {
  const { bg, color } = getLeadStatusChipStyle(status);
  return { bg, color };
};

const DESKTOP_DRAWER_STATUS_OPTIONS = LEAD_STATUS_DEFINITIONS.map((d) => ({
  value: d.value,
  label: d.value,
  icon: d.Icon,
  color: d.color,
}));

const desktopDrawerStatLabelSx = { fontSize: 11, fontWeight: 500, color: "text.secondary", mb: 0.35 };
const desktopDrawerStatValueSx = { fontSize: 16, fontWeight: 600, lineHeight: 1.2 };

const getPinColor = (status = "New", notes = "") => {
  const normalized = status.trim().toLowerCase();
  
  // Special handling for "New" status with specific note patterns
  if (normalized === "new" && notes) {
    const notesLower = notes.toLowerCase();
    
    if (notesLower.includes("adt")) {
      return "#F5D327"; // Yellow
    }
    if (notesLower.includes("title company") || notesLower.includes("realtor")) {
      return "#A627F5"; // Purple
    }
    if (notesLower.includes("builder relationship")) {
      return "#F527C8"; // Pink
    }
  }
  
  // Default status color lookup
  const match = Object.entries(STATUS_COLORS).find(
    ([key]) => key.toLowerCase() === normalized
  );
  return match ? match[1] : "#42A5F5";
};

// ---------------- MAP HELPERS ----------------
function FitBoundsOnData({ leads, userZoomedRef, initialFitRef, fitModeRef, didFitLeadsRef }) {
  const map = useMap();

  useEffect(() => {
    if (userZoomedRef.current) return;
    if (!initialFitRef.current) return;
    if (!leads || leads.length === 0) return;

    const validLeads = leads.filter(leadHasMapCoordinates);

    if (!validLeads.length) return;

    const bounds = L.latLngBounds(
      validLeads.map((l) => [
        l.latitude || l.lat,
        l.longitude || l.lng,
      ])
    );

    if (bounds.isValid()) {
      initialFitRef.current = false;
      if (didFitLeadsRef) didFitLeadsRef.current = true;
      try {
        map.stop();
        map.fitBounds(bounds, {
          padding: [50, 50],
          maxZoom: fitModeRef.current === "filtered" ? 13 : 10,
          animate: false,
        });
      } catch (e) {
        // ignore stale map reference
      }
    }
  }, [leads, map, userZoomedRef, initialFitRef, fitModeRef, didFitLeadsRef]);

  return null;
}

function CenterOnSelectedLead({ selectedLeads, userZoomedRef }) {
  const map = useMap();
  
  useEffect(() => {
    // Only center/zoom if exactly one lead is selected
    if (selectedLeads && selectedLeads.length === 1) {
      const lead = selectedLeads[0];
      
      const lat = lead.latitude || lead.lat;
      const lng = lead.longitude || lead.lng;
      
      if (lat != null && lng != null) {
        userZoomedRef.current = true;
        try {
          map.stop();
          map.setView([lat, lng], 12, { animate: false });
        } catch (e) {
          // ignore stale map reference
        }
      }
    }
    // When no leads selected or multiple selected, manual zoom centers on map center (default Leaflet behavior)
  }, [selectedLeads, map, userZoomedRef]);
  
  return null;
}

function MapClickHandler({ onMapClick }) {
  useMapEvents({
    click: onMapClick,
  });
  return null;
}

// ---------------- MAIN COMPONENT ----------------
const Leads = ({ token, currentUser }) => {
  // ========== STATE FROM LEADS.JS ==========
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [leafletMapView, setLeafletMapView] = useState(() => ({
    center: [...LEADS_MAP_CONTINENTAL_US.center],
    zoom: LEADS_MAP_CONTINENTAL_US.zoom,
  }));
  /** Bump only when restoring a saved last view (never for company HQ). */
  const [leafletMapKey, setLeafletMapKey] = useState(0);
  const [companyHqView, setCompanyHqView] = useState(null);
  const [startupGpsResolved, setStartupGpsResolved] = useState(false);
  const [territories, setTerritories] = useState([]);
  const [selectedTerritories, setSelectedTerritories] = useState([]);
  const primaryTerritorySeededRef = useRef(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [leadToDelete, setLeadToDelete] = useState(null);

  // ========== STATE FROM LEADMAP.JS ==========
  const navigate = useNavigate();
  const theme = useTheme();

const glassyPanelSx = {
  bgcolor:
    theme.palette.mode === "dark"
      ? "rgba(30, 41, 59, 0.85)"
      : "rgba(255,255,255,0.85)",

  backdropFilter: "blur(10px)",
  WebkitBackdropFilter: "blur(10px)",

  border: "1px solid",
  borderColor:
    theme.palette.mode === "dark"
      ? "rgba(255,255,255,0.12)"
      : "rgba(0,0,0,0.12)",

  boxShadow:
    theme.palette.mode === "dark"
      ? "0 10px 30px rgba(0,0,0,0.55)"
      : "0 10px 30px rgba(0,0,0,0.18)",
};

// ---------------- GLASSY BUTTONS ----------------
const glassyButtonSx = {
  borderRadius: "10px",
  px: 2,
  py: 0.9,
  textTransform: "none",
  fontWeight: 500,
  fontSize: "13px",

  bgcolor:
    theme.palette.mode === "dark"
      ? "rgba(255,255,255,0.08)"
      : "rgba(255,255,255,0.75)",

  color: "text.primary",

  border: "1px solid",
  borderColor:
    theme.palette.mode === "dark"
      ? "rgba(255,255,255,0.12)"
      : "rgba(0,0,0,0.12)",

  boxShadow:
    theme.palette.mode === "dark"
      ? "0 4px 12px rgba(0,0,0,0.3)"
      : "0 2px 8px rgba(0,0,0,0.15)",

  backdropFilter: "blur(8px)",
  WebkitBackdropFilter: "blur(8px)",

  "&:hover": {
    bgcolor:
      theme.palette.mode === "dark"
        ? "rgba(255,255,255,0.12)"
        : "rgba(255,255,255,0.9)",
  },
};

const desktopFilterButtonSx = {
  borderRadius: "16px",
  px: 2.2,
  py: 1.05,
  textTransform: "none",
  fontWeight: 700,
  boxShadow: "0 8px 22px rgba(0,0,0,0.14)",
};

const desktopGeoFabSx = {
  position: "absolute",
  right: 16,
  bottom: 16,
  zIndex: 1000,
  borderRadius: "16px",
  px: 2.1,
  py: 1.05,
  textTransform: "none",
  fontWeight: 700,
  fontSize: "0.92rem",
  color: "text.primary",
  backgroundColor:
    theme.palette.mode === "dark"
      ? "rgba(231, 172, 207, 0.28)"
      : "#e7accf",
  border: "1px solid",
  borderColor:
    theme.palette.mode === "dark"
      ? "rgba(255,255,255,0.10)"
      : "rgba(0,0,0,0.06)",
  backdropFilter: "blur(14px)",
  WebkitBackdropFilter: "blur(14px)",
  boxShadow:
    theme.palette.mode === "dark"
      ? "0 10px 24px rgba(0,0,0,0.35)"
      : "0 10px 24px rgba(0,0,0,0.14)",
  "&:hover": {
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(231, 172, 207, 0.34)"
        : "#eab8d5",
  },
};

/** Single-layer map control: avoid nested hit targets + pointer-events hacks (Leaflet / selection bugs). */
const desktopMyLocationIconButtonSx = {
  width: 42,
  height: 42,
  p: 0,
  borderRadius: "16px",
  userSelect: "none",
  WebkitUserSelect: "none",
  MozUserSelect: "none",
  msUserSelect: "none",
  color: "text.primary",
  backgroundColor:
    theme.palette.mode === "dark"
      ? "rgba(255,255,255,0.10)"
      : "rgba(255,255,255,0.92)",
  border: "1px solid",
  borderColor:
    theme.palette.mode === "dark"
      ? "rgba(255,255,255,0.14)"
      : "rgba(0,0,0,0.08)",
  backdropFilter: "blur(14px)",
  WebkitBackdropFilter: "blur(14px)",
  boxShadow:
    theme.palette.mode === "dark"
      ? "0 8px 18px rgba(0,0,0,0.45)"
      : "0 8px 18px rgba(0,0,0,0.14)",
  "&:hover": {
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(255,255,255,0.16)"
        : "rgba(255,255,255,1)",
  },
  "&.Mui-disabled": {
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(255,255,255,0.08)"
        : "rgba(255,255,255,0.85)",
  },
};

const desktopFilterRowButtonSx = {
  justifyContent: "space-between",
  textTransform: "none",
  borderRadius: "16px",
  px: 1.25,
  py: 1.15,
  color: "text.primary",
  bgcolor: "transparent",
  "&:hover": {
    bgcolor:
      theme.palette.mode === "dark"
        ? "rgba(255,255,255,0.04)"
        : "rgba(0,0,0,0.04)",
  },
};

const compactDateFieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    height: 40,
    background:
      theme.palette.mode === "dark"
        ? "rgba(15,23,42,0.88)"
        : "rgba(255,255,255,0.9)",
  },
  "& .MuiOutlinedInput-input": {
    py: "8px",
    fontSize: "0.9rem",
  },
};


  // Keep page render mode aligned with the app shell (phone/tablet compact mode).
  const isMobileWidth = useMediaQuery(theme.breakpoints.down("lg"));
  const isShortHeight = useMediaQuery("(max-height: 500px)");
  const isMobile = isMobileWidth || isShortHeight;
  const isSmallScreen = isMobile;

  const isLandscape = useLandscape();
  const { role, canViewAdditionalNotes, user } = useAuth();
  
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [routeCoordinates, setRouteCoordinates] = useState([]);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [routePlanned, setRoutePlanned] = useState(false);
  const [showRouteBuilder, setShowRouteBuilder] = useState(false);
  const [selectedLeadForDrawer, setSelectedLeadForDrawer] = useState(null);
  const [desktopDrawerExpanded, setDesktopDrawerExpanded] = useState(false);
  const [desktopDrawerEditData, setDesktopDrawerEditData] = useState({});
  const [desktopDrawerSaving, setDesktopDrawerSaving] = useState(false);
  const [displayLead, setDisplayLead] = useState(null);
  const [boxSelectMode, setBoxSelectMode] = useState(false);
  const [selectionBox, setSelectionBox] = useState(null);
  const [isDrawingBox, setIsDrawingBox] = useState(false);
  const [boxStart, setBoxStart] = useState(null);
  const [legendOpen, setLegendOpen] = useState(false);
  const [selectedStatusFilters, setSelectedStatusFilters] = useState(new Set());
  const [soldDateFilter, setSoldDateFilter] = useState(null); // null, '7days', '14days', or 'custom'
  const [soldDateRange, setSoldDateRange] = useState({ start: null, end: null });
  const [soldDateMenuAnchor, setSoldDateMenuAnchor] = useState(null);
  const [soldDateButtonAnchor, setSoldDateButtonAnchor] = useState(null);
  const [territoryMenuAnchor, setTerritoryMenuAnchor] = useState(null);
  const [desktopFiltersOpen, setDesktopFiltersOpen] = useState(false);
  const [desktopFiltersAnchorEl, setDesktopFiltersAnchorEl] = useState(null);
  const [showDesktopStatusSection, setShowDesktopStatusSection] = useState(true);
  const [showDesktopDateSection, setShowDesktopDateSection] = useState(false);
  const [showDesktopTerritorySection, setShowDesktopTerritorySection] = useState(false);
  const [showDesktopCustomDateFields, setShowDesktopCustomDateFields] = useState(false);
  const [viewLeadZoom, setViewLeadZoom] = useState(null);
  const [geocodeMissingLoading, setGeocodeMissingLoading] = useState(false);
  const [desktopLocating, setDesktopLocating] = useState(false);
  const [desktopUserLocation, setDesktopUserLocation] = useState(null);
  const [desktopLocationApproximate, setDesktopLocationApproximate] = useState(false);
  const [leadDateSourceLeads, setLeadDateSourceLeads] = useState([]);
  const leadsRef = useRef(leads);
  leadsRef.current = leads;

  // ========== REFS ==========
  const mapRef = useRef(null);
  const userZoomedRef = useRef(false);
  const initialFitRef = useRef(true);
  const fitModeRef = useRef("global"); // "global" | "filtered" — guides zoom (wider for all leads, tighter for territory)
  const soldDateButtonRef = useRef(null);
  const desktopAutoLocateAttemptedRef = useRef(false);
  const suppressDesktopAutoLocateRef = useRef(false);
  const restoredLastViewRef = useRef(false);
  const didFitLeadsRef = useRef(false);
  const ignoreNextAutoFitRef = useRef(false);

  const [searchParams, setSearchParams] = useSearchParams();

  const viewportFetchEnabled = useMemo(
    () => isLeadsViewportMapFetchEnabled(searchParams),
    [searchParams]
  );
  const desktopClusterFeatureEnabled = useMemo(
    () => isLeadsMapClusteringEnabled(searchParams),
    [searchParams]
  );
  const mobileClusterFeatureEnabled = useMemo(
    () => isLeadsMapMobileClusteringEnabled(searchParams),
    [searchParams]
  );
  const desktopClusterActive = desktopClusterFeatureEnabled && !boxSelectMode;

  const [viewportRefreshTick, setViewportRefreshTick] = useState(0);

  /** Single territory id for optional server-side map-data filter (multi-select stays client-only). */
  const territoryIdForViewport =
    selectedTerritories.length === 1 ? selectedTerritories[0] : null;

  // ========== DATA FETCHING (FROM LEADS.JS) ==========
  const applyMapData = useCallback((raw) => {
    setLeads(Array.isArray(raw) ? raw : []);
  }, []);

  const fetchLeads = useCallback(async () => {
    try {
      setLoading(true);
      applyMapData(await fetchMapPins());
    } catch (err) {
      console.error("Error fetching leads:", err);
      setLeads([]);
      const isTimeout = err?.code === "ECONNABORTED";
      tError(
        isTimeout
          ? "Loading leads timed out — try again or check the API server."
          : "Failed to load leads"
      );
    } finally {
      setLoading(false);
    }
  }, [applyMapData]);

  const fetchTerritories = async () => {
    try {
      const res = await API.get("/territories/");
      setTerritories(res.data || []);
    } catch (err) {
      console.error("Error fetching territories:", err);
    }
  };

  const desktopCanLocate = user?.id != null;
  const desktopApproxAccuracyThresholdM = 300;

  const centerDesktopMapOnLocation = useCallback((coords, { auto = false } = {}) => {
    if (!mapRef.current) {
      if (!auto) tError("Map is not ready yet.");
      return false;
    }
    const lat = Number(coords?.latitude);
    const lng = Number(coords?.longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      if (!auto) tError("Invalid location coordinates.");
      return false;
    }
    userZoomedRef.current = true;
    try {
      mapRef.current.stop();
      const maxZoom = mapRef.current.getMaxZoom?.() ?? 18;
      const zoom = Math.min(maxZoom, USER_LOCATION_LOCATE_ZOOM);
      mapRef.current.setView([lat, lng], zoom, {
        animate: true,
        duration: 0.4,
      });
      return true;
    } catch (err) {
      console.error("Failed to center map on location:", err);
      if (!auto) tError("Could not center map on your location.");
      return false;
    }
  }, []);

  const getBestDesktopPosition = useCallback(
    ({ sampleMs = 8000, maxWaitMs = 15000 } = {}) =>
      new Promise((resolve, reject) => {
        if (!navigator?.geolocation) {
          reject({ code: 0, message: "Geolocation unavailable." });
          return;
        }

        let bestPos = null;
        let lastErr = null;
        let settled = false;
        let sampleTimer = null;
        let hardTimer = null;
        const startedAt = Date.now();

        const finalize = () => {
          if (settled) return;
          settled = true;
          if (watchId != null) {
            try {
              navigator.geolocation.clearWatch(watchId);
            } catch (_) {
              /* ignore */
            }
          }
          if (sampleTimer) clearTimeout(sampleTimer);
          if (hardTimer) clearTimeout(hardTimer);
          if (bestPos) {
            resolve(bestPos);
            return;
          }
          reject(lastErr || { code: 3, message: "Timed out getting location." });
        };

        const getAccuracy = (p) => {
          const acc = Number(p?.coords?.accuracy);
          return Number.isFinite(acc) ? acc : Number.POSITIVE_INFINITY;
        };

        const watchId = navigator.geolocation.watchPosition(
          (pos) => {
            if (!bestPos || getAccuracy(pos) < getAccuracy(bestPos)) {
              bestPos = pos;
            }
            if (Date.now() - startedAt >= sampleMs && bestPos) {
              finalize();
            }
          },
          (err) => {
            lastErr = err;
            if (err?.code === 1) {
              finalize();
            }
          },
          {
            enableHighAccuracy: true,
            timeout: maxWaitMs,
            maximumAge: 0,
          }
        );

        sampleTimer = setTimeout(finalize, sampleMs);
        hardTimer = setTimeout(finalize, maxWaitMs + 1000);
      }),
    []
  );

  const handleDesktopLocate = useCallback(async (auto = false) => {
    if (!desktopCanLocate || isMobile || desktopLocating) return;
    if (!navigator?.geolocation) {
      if (!auto) tError("Location is not supported in this browser.");
      return;
    }
    setDesktopLocating(true);
    try {
      const pos = await getBestDesktopPosition({ sampleMs: 8000, maxWaitMs: 15000 });
      const lat = Number(pos?.coords?.latitude);
      const lng = Number(pos?.coords?.longitude);
      const accuracy = Number(pos?.coords?.accuracy);
      const approximate =
        !Number.isFinite(accuracy) || accuracy > desktopApproxAccuracyThresholdM;
      setDesktopLocationApproximate(approximate);
      if (Number.isFinite(lat) && Number.isFinite(lng)) {
        setDesktopUserLocation({
          lat,
          lng,
          accuracy: Number.isFinite(accuracy) ? accuracy : null,
        });
      }
      if (!auto && approximate) {
        const suffix = Number.isFinite(accuracy) ? ` (~${Math.round(accuracy)}m)` : "";
        toast.info(`Approximate location${suffix}`);
      }
      const centered = centerDesktopMapOnLocation(pos.coords, { auto });
      if (!centered && !auto) return;
    } catch (err) {
      const code = err?.code;
      if (code === 1) {
        try {
          const until = Date.now() + 24 * 60 * 60 * 1000;
          localStorage.setItem("tc_leads_desktop_loc_deny_until", String(until));
        } catch (_) {
          /* ignore */
        }
        if (!auto) tError("Location permission denied.");
      } else if (code === 3) {
        if (!auto) tError("Timed out getting location.");
      } else if (!auto) {
        tError("Location unavailable.");
      }
    } finally {
      setDesktopLocating(false);
    }
  }, [
    desktopCanLocate,
    isMobile,
    desktopLocating,
    centerDesktopMapOnLocation,
    getBestDesktopPosition,
    desktopApproxAccuracyThresholdM,
  ]);

  // ========== EFFECTS ==========

  useEffect(() => {
    if (viewportFetchEnabled) {
      // Map must mount so MapViewportFetcher can run; do not hold global loading (that hid the map).
      return;
    }
    fetchLeads();
  }, [viewportFetchEnabled, fetchLeads]);

  // Non-viewport: lead-date chips derive from loaded map pins (must re-sync when leads load).
  useEffect(() => {
    if (viewportFetchEnabled) return;
    setLeadDateSourceLeads(Array.isArray(leads) ? leads : []);
  }, [viewportFetchEnabled, leads]);

  // Viewport: date options from lightweight lead_dates fetch (not bbox pins).
  useEffect(() => {
    if (!viewportFetchEnabled) return;

    let cancelled = false;
    (async () => {
      try {
        const list = await fetchLeadDateSourcePins();
        if (cancelled) return;
        setLeadDateSourceLeads(list);
      } catch (err) {
        if (cancelled) return;
        setLeadDateSourceLeads(
          Array.isArray(leadsRef.current) ? leadsRef.current : []
        );
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [viewportFetchEnabled, viewportRefreshTick, user?.id]);

  // Handle URL parameters to zoom to specific lead (View on Map from LeadDetailPage)
  useEffect(() => {
    const leadId = searchParams.get('leadId');
    
    if (leadId && leads.length > 0) {
      // Keep desktop auto-locate from overriding explicit "View on Map".
      suppressDesktopAutoLocateRef.current = true;
      const targetLead = leads.find(l => l.id === parseInt(leadId));
      
      if (targetLead && !selectedLeads.some(l => l.id === targetLead.id)) {
        // Skip FitBoundsOnData so it doesn't zoom to whole map - CenterOnSelectedLead will handle zoom
        initialFitRef.current = false;
        const zoomParam = searchParams.get('zoom');
        const zoom = zoomParam ? parseInt(zoomParam, 10) : 15;
        if (!isNaN(zoom) && zoom >= 1 && zoom <= 20) {
          setViewLeadZoom(zoom);
        } else {
          setViewLeadZoom(15);
        }
        setSelectedLeads([targetLead]);
        setSearchParams({});
      }
    }
  }, [leads, searchParams, selectedLeads, setSearchParams]);

  useEffect(() => {
    if (["Admin", "Sales Director", "Manager"].includes(role)) {
      fetchTerritories();
    }
  }, [role]);

  // Sales Rep / Manager: start with primary territory selected (same as territory filter).
  useEffect(() => {
    if (primaryTerritorySeededRef.current) return;
    if (!["Sales Rep", "Manager"].includes(role)) return;
    const pid = user?.primary_territory ?? user?.territory_id;
    if (pid == null || pid === "") return;
    const id = Number(pid);
    if (!Number.isFinite(id)) return;
    primaryTerritorySeededRef.current = true;
    setSelectedTerritories((prev) => (prev.length > 0 ? prev : [id]));
  }, [role, user]);

  // Restore last camera for any role. Remount is OK here; never remount for HQ.
  useEffect(() => {
    const uid = user?.id;
    if (uid == null || restoredLastViewRef.current) return;
    const saved = readLeadsMapLastView(uid);
    if (!saved) return;
    restoredLastViewRef.current = true;
    initialFitRef.current = false;
    userZoomedRef.current = true;
    ignoreNextAutoFitRef.current = true;
    setLeafletMapView(saved);
    setLeafletMapKey((k) => k + 1);
  }, [user?.id]);

  // Company HQ coords are a last-resort fallback (setView), not a MapContainer remount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await API.get("/company-profile/");
        if (cancelled) return;
        const parsed = parseCompanyMapDefault(res.data);
        if (parsed) setCompanyHqView(parsed);
      } catch {
        /* continental US remains the mount default */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const unsubscribe = subscribeLeadUpdates(() => {
      if (viewportFetchEnabled) {
        setViewportRefreshTick((t) => t + 1);
      } else {
        fetchLeads();
      }
    });
    return () => unsubscribe();
  }, [viewportFetchEnabled, fetchLeads]);

  useEffect(() => {
    const mapContainer = document.querySelector(".leaflet-container");
    if (!mapContainer || !mapRef.current) return;

    const map = mapRef.current;

    if (boxSelectMode) {
      // Disable Leaflet interactions
      map.dragging.disable();
      map.touchZoom.disable();
      map.doubleClickZoom.disable();
      map.scrollWheelZoom.disable();
      map.boxZoom.disable();
      map.keyboard.disable();

      // Disable browser native gestures
      mapContainer.style.touchAction = "none";
      mapContainer.style.webkitUserSelect = "none";
      mapContainer.style.userSelect = "none";
      mapContainer.style.webkitTouchCallout = "none";
      mapContainer.style.webkitTapHighlightColor = "transparent";
    } else {
      // Re-enable Leaflet interactions
      map.dragging.enable();
      map.touchZoom.enable();
      map.doubleClickZoom.enable();
      map.scrollWheelZoom.enable();
      map.boxZoom.enable();
      map.keyboard.enable();

      // Restore browser defaults
      mapContainer.style.touchAction = "auto";
      mapContainer.style.webkitUserSelect = "auto";
      mapContainer.style.userSelect = "auto";
      mapContainer.style.webkitTouchCallout = "default";
      mapContainer.style.webkitTapHighlightColor = "initial";
    }
  }, [boxSelectMode]);

  // Move zoom controls down on mobile to prevent clipping
  useEffect(() => {
    if (!isMobile) return;

    const styleId = "leadmap-zoom-control-mobile";
    const existingStyle = document.getElementById(styleId);
    if (existingStyle) {
      existingStyle.remove();
    }

    const style = document.createElement("style");
    style.id = styleId;
    style.textContent = `
      @media (max-width: 768px) {
        .leaflet-control-zoom {
          top: 10px !important;
        }
      }
    `;
    document.head.appendChild(style);

    return () => {
      const styleToRemove = document.getElementById(styleId);
      if (styleToRemove) {
        styleToRemove.remove();
      }
    };
  }, [isMobile]);


  // ========== FILTERING LOGIC ==========
  // First: Territory filtering (from Leads.js)
  const territoryFilteredLeads = useMemo(() => {
    const list = Array.isArray(leads) ? leads : [];
    if (selectedTerritories.length > 0) {
      return list.filter((lead) => selectedTerritories.includes(lead.territory));
    }
    return list;
  }, [leads, selectedTerritories]);

  // Date option source is intentionally independent from viewport-loaded pins.
  const territoryFilteredLeadDateSource = useMemo(() => {
    const list = Array.isArray(leadDateSourceLeads) ? leadDateSourceLeads : [];
    if (selectedTerritories.length > 0) {
      const selectedIds = new Set(selectedTerritories.map((id) => Number(id)));
      return list.filter((lead) => selectedIds.has(Number(lead.territory)));
    }
    return list;
  }, [leadDateSourceLeads, selectedTerritories]);

  // Last 3 distinct upload dates (created_at, local date only) from territory-filtered leads, newest first
  const lastThreeUploadDates = useMemo(() => {
    const toLocalDateStr = (d) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    const dateSet = new Set();
    territoryFilteredLeadDateSource.forEach((lead) => {
      const raw = lead.created_at;
      if (!raw) return;
      const d = new Date(raw);
      if (!isNaN(d.getTime())) dateSet.add(toLocalDateStr(d));
    });
    return [...dateSet].sort((a, b) => b.localeCompare(a)).slice(0, 3);
  }, [territoryFilteredLeadDateSource]);

  // Helper function to check if a lead matches a status filter
  const leadMatchesStatus = useCallback((lead, statusKey) => {
    const normalized = (lead.status || "New").trim().toLowerCase();
    const notes = (lead.notes || "").toLowerCase();
    
    if (statusKey === "new-adt") {
      return normalized === "new" && notes.includes("adt");
    }
    if (statusKey === "new-title-realtor") {
      return normalized === "new" && (notes.includes("title company") || notes.includes("realtor"));
    }
    if (statusKey === "new-builder") {
      return normalized === "new" && notes.includes("builder relationship");
    }
    
    if (statusKey === "new") {
      return normalized === "new" && 
        !notes.includes("adt") && 
        !notes.includes("title company") && 
        !notes.includes("realtor") && 
        !notes.includes("builder relationship");
    }
    
    return normalized === statusKey.toLowerCase();
  }, []);

  // Helper: match lead by Lead Date (created_at / upload date); use local date strings to match SearchLeadsPage
  const leadMatchesLeadDateFilter = useCallback((lead) => {
    if (!soldDateFilter) return true;
    const createdAt = lead.created_at;
    if (!createdAt) return false;
    const d = new Date(createdAt);
    if (isNaN(d.getTime())) return false;
    const leadDateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

    if (soldDateFilter === "custom") {
      if (!soldDateRange.start || !soldDateRange.end) return true;
      return leadDateStr >= soldDateRange.start && leadDateStr <= soldDateRange.end;
    }

    // Single date: YYYY-MM-DD (one of last 3 upload dates)
    if (/^\d{4}-\d{2}-\d{2}$/.test(soldDateFilter)) {
      return leadDateStr === soldDateFilter;
    }

    return true;
  }, [soldDateFilter, soldDateRange]);

  // Client filters on slim map pins: status, notes, territory, created_at, lat/lng.
  // in_other_rep_route is only on GET /leads/:id/ (drawer); omitted on map-data by design.
  const filteredLeads = useMemo(() => {
    let result = territoryFilteredLeads.filter((lead) => !lead.in_other_rep_route);
    
    // Apply lead date filter (created_at)
    if (soldDateFilter) {
      result = result.filter(lead => leadMatchesLeadDateFilter(lead));
    }
    
    // Apply status filters
    if (selectedStatusFilters.size === 0) {
      return result;
    }
    
    return result.filter(lead => {
      return Array.from(selectedStatusFilters).some(statusKey => 
        leadMatchesStatus(lead, statusKey)
      );
    });
  }, [territoryFilteredLeads, selectedStatusFilters, leadMatchesStatus, soldDateFilter, leadMatchesLeadDateFilter]);

  // GPS only after pins/last-view missed — never override a territory fit.
  useEffect(() => {
    if (isMobile) return;
    if (user?.id == null) return;
    if (loading) return;
    if (searchParams.get("leadId")) return;
    if (suppressDesktopAutoLocateRef.current) {
      setStartupGpsResolved(true);
      return;
    }
    if (restoredLastViewRef.current || didFitLeadsRef.current) {
      setStartupGpsResolved(true);
      return;
    }
    if ((filteredLeads || []).some(leadHasMapCoordinates)) {
      return;
    }
    if (!viewportFetchEnabled && leads.length === 0) {
      setStartupGpsResolved(true);
      return;
    }
    if (desktopAutoLocateAttemptedRef.current) return;
    desktopAutoLocateAttemptedRef.current = true;

    if (!desktopCanLocate) {
      setStartupGpsResolved(true);
      return;
    }
    try {
      const denyUntil = Number(localStorage.getItem("tc_leads_desktop_loc_deny_until") || 0);
      if (Number.isFinite(denyUntil) && Date.now() < denyUntil) {
        setStartupGpsResolved(true);
        return;
      }
    } catch (_) {
      /* ignore */
    }

    Promise.resolve(handleDesktopLocate(true)).finally(() => {
      setStartupGpsResolved(true);
    });
  }, [
    isMobile,
    loading,
    searchParams,
    filteredLeads,
    leads.length,
    viewportFetchEnabled,
    desktopCanLocate,
    handleDesktopLocate,
    user?.id,
  ]);

  // Set fit mode when territory changes; allow re-fit unless a single lead is selected (don't override center-on-selected-lead).
  // Skip when "View on Map" (leadId in URL) — CenterOnSelectedLead handles zoom.
  // useLayoutEffect so refs are set before FitBoundsOnData's useEffect runs (fixes territory-select zoom).
  useLayoutEffect(() => {
    if (searchParams.get("leadId")) return;

    if (selectedTerritories.length > 0) {
      fitModeRef.current = "filtered";
    } else {
      fitModeRef.current = "global";
    }

    // Viewport/bbox mode: user controls pan/zoom; do not re-arm auto-fit when route selection changes
    // (that caused fitBounds loops as bbox loads grew filteredLeads).
    if (viewportFetchEnabled) {
      return;
    }

    if (selectedLeads.length !== 1) {
      if (ignoreNextAutoFitRef.current) {
        ignoreNextAutoFitRef.current = false;
        return;
      }
      userZoomedRef.current = false;
      initialFitRef.current = true;
    }
  }, [selectedTerritories, selectedLeads.length, searchParams, viewportFetchEnabled]);

  // Viewport mode territory switch UX:
  // when a single territory is selected, fetch its full map slice (no bbox)
  // so the map can fit to that territory automatically.
  useEffect(() => {
    if (!viewportFetchEnabled) return;
    if (searchParams.get("leadId")) return;
    if (selectedTerritories.length !== 1) return;
    if (ignoreNextAutoFitRef.current) {
      ignoreNextAutoFitRef.current = false;
      return;
    }

    let cancelled = false;
    const territoryId = selectedTerritories[0];

    (async () => {
      try {
        userZoomedRef.current = false;
        initialFitRef.current = true;
        fitModeRef.current = "filtered";
        setLoading(true);
        const list = await fetchMapPins({ territory: territoryId });
        if (cancelled) return;
        applyMapData(list);
      } catch (err) {
        if (cancelled) return;
        console.error("Territory bootstrap fetch failed:", err);
        const isTimeout = err?.code === "ECONNABORTED";
        tError(
          isTimeout
            ? "Loading territory leads timed out — try again."
            : "Failed to load selected territory"
        );
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [viewportFetchEnabled, selectedTerritories, searchParams, applyMapData]);

  // ========== HANDLERS ==========
  const handleDelete = (id) => {
    const lead = leads.find((l) => l.id === id);
    setLeadToDelete(lead);
    setConfirmDelete(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await API.delete(`leads/${leadToDelete.id}/`);
      tSuccess("Lead archived successfully");
      if (selectedLeadForDrawer?.id === leadToDelete.id) {
        setSelectedLeadForDrawer(null);
      }
      setLeads((prev) => prev.filter((l) => l.id !== leadToDelete.id));
      if (viewportFetchEnabled) {
        setViewportRefreshTick((t) => t + 1);
      } else {
        fetchLeads();
      }
    } catch (err) {
      console.error("Error deleting lead:", err);
      tError(err.response?.data?.detail || "Something went wrong while archiving lead");
    } finally {
      setConfirmDelete(false);
      setLeadToDelete(null);
    }
  };

  const toggleBoxSelect = () => {
    setBoxSelectMode((prev) => !prev);
  };

  const handleSaveRouteClick = () => {
    setShowSaveModal(true);
  };

  const planRoute = useCallback(async (leadsToRoute) => {
    const coords = leadsToRoute
      .map(
        (lead) =>
          `${lead.longitude || lead.lng},${lead.latitude || lead.lat}`
      )
      .join(";");

    try {
      const url = `https://router.project-osrm.org/route/v1/driving/${coords}?overview=full&geometries=geojson`;
      const res = await axios.get(url);
      if (!res.data?.routes?.length) {
        toast.error("❌ Routing service returned no route.");
        return false;
      }
      const points = res.data.routes[0].geometry.coordinates.map((c) => [
        c[1],
        c[0],
      ]);
      setRouteCoordinates(points);
      return points;
    } catch (err) {
      console.error("Route planning failed:", err);
      toast.error("❌ Failed to plan route.");
      return false;
    }
  }, []);

  const handlePlanRoute = useCallback(async () => {
    if (!selectedLeads || selectedLeads.length < 2) {
      toast.info("Select at least two leads for routing.");
      return;
    }

    const validLeads = selectedLeads.filter(
      (l) => (l.latitude || l.lat) && (l.longitude || l.lng)
    );
    if (validLeads.length < 2) {
      toast.error("❌ Not enough valid coordinates to plan a route.");
      return;
    }

    const route = await planRoute(validLeads);
    if (route) {
      setRoutePlanned(true);
      toast.success("✅ Route planned successfully!");
    }
  }, [selectedLeads, planRoute]);

  // Desktop drawer: seed from map pin, then always load full lead from GET /leads/:id/
  useEffect(() => {
    if (!selectedLeadForDrawer || isMobile) return;

    const leadId = selectedLeadForDrawer.id;
    const seed = selectedLeadForDrawer;

    const applyDesktopDrawerFromLead = (lead) => {
      const incompleteFromList = (lead.followups || []).find((f) => !f.completed);
      const followUpDateFromList = incompleteFromList?.due_date
        ? new Date(incompleteFromList.due_date).toISOString().split("T")[0]
        : "";
      setDesktopDrawerEditData({
        first_name: lead.first_name || "",
        last_name: lead.last_name || "",
        email: lead.email || "",
        phone: lead.phone || "",
        status: lead.status || "New",
        notes: lead.notes || "",
        sms_email_opt_in: lead.sms_email_opt_in ?? "",
        followUpDate: followUpDateFromList,
      });
    };

    setDisplayLead(seed);
    applyDesktopDrawerFromLead(seed);
    setDesktopDrawerExpanded(false);

    let cancelled = false;
    (async () => {
      try {
        const res = await API.get(`/leads/${leadId}/`);
        if (cancelled) return;
        const full = res.data;
        setSelectedLeadForDrawer(full);
        setDisplayLead(full);
        applyDesktopDrawerFromLead(full);
      } catch (_) {
        /* keep map-seeded drawer if detail fails */
      }
    })();

    return () => {
      cancelled = true;
    };
    // intentionally do NOT clear displayLead when null — let animation finish
  }, [selectedLeadForDrawer?.id, isMobile]);

  const handleDesktopDrawerSave = useCallback(async () => {
    if (!selectedLeadForDrawer) return;
    if ((desktopDrawerEditData.phone || "").trim() && !desktopDrawerEditData.sms_email_opt_in) {
      tError("Please select SMS/Email Opt-In when a phone number is present");
      return;
    }
    if (desktopDrawerEditData.status === "Follow Up Needed" && !desktopDrawerEditData.followUpDate) {
      tError("Please select a follow-up date");
      return;
    }
    setDesktopDrawerSaving(true);
    try {
      const payload = {
        first_name: (desktopDrawerEditData.first_name || "").trim(),
        last_name: (desktopDrawerEditData.last_name || "").trim(),
        email: (desktopDrawerEditData.email || "").trim() || null,
        phone: (desktopDrawerEditData.phone || "").trim() || null,
        status: desktopDrawerEditData.status || "New",
        notes: desktopDrawerEditData.notes || "",
        sms_email_opt_in: desktopDrawerEditData.sms_email_opt_in || null,
      };
      const res = await API.patch(`/leads/${selectedLeadForDrawer.id}/`, payload);
      const leadId = selectedLeadForDrawer.id;
      let finalLead = res.data;

      if (desktopDrawerEditData.status === "Follow Up Needed" && desktopDrawerEditData.followUpDate) {
        try {
          const followupsFromResponse = res.data.followups || [];
          const incompleteFollowUp = followupsFromResponse.find((f) => !f.completed);

          if (incompleteFollowUp) {
            const isInitialSchedule = !incompleteFollowUp.due_date;
            await API.patch(`/followups/${incompleteFollowUp.id}/`, {
              due_date: desktopDrawerEditData.followUpDate,
              notes: isInitialSchedule
                ? `Follow-up scheduled for ${desktopDrawerEditData.followUpDate}`
                : `Follow-up rescheduled to ${desktopDrawerEditData.followUpDate}`,
            });
            toast.success("Lead and follow-up updated");
          } else {
            await API.post("/followups/", {
              lead: leadId,
              due_date: desktopDrawerEditData.followUpDate,
              completed: false,
              notes: `Follow-up scheduled for ${desktopDrawerEditData.followUpDate}`,
            });
            toast.success("Lead updated; follow-up created");
          }
          try {
            const refreshed = await API.get(`/leads/${leadId}/`);
            finalLead = refreshed.data;
          } catch (_) {
            /* keep PATCH response */
          }
        } catch (followUpErr) {
          console.error("Desktop drawer follow-up save failed:", followUpErr);
          toast.warning("Lead updated but follow-up could not be saved");
        }
      } else {
        toast.success("Lead updated");
      }

      emitLeadUpdate(finalLead);
      setSelectedLeadForDrawer(null);
      setDesktopDrawerExpanded(false);
    } catch (err) {
      toast.error("Failed to save lead");
    } finally {
      setDesktopDrawerSaving(false);
    }
  }, [selectedLeadForDrawer, desktopDrawerEditData]);

  const handleMarkerClick = useCallback((lead) => {
    if (lead.in_other_rep_route) {
      toast.info("This lead is already in another sales rep's route");
      return;
    }
    // Both mobile and desktop open the drawer on pin click
    setSelectedLeadForDrawer(lead);
  }, []);

  const handleRemoveFromRoute = useCallback((id) => {
    setSelectedLeads((prevSelected) =>
      prevSelected.filter((l) => l.id !== id)
    );
  }, []);

  const handleClearRoute = useCallback(() => {
    setSelectedLeads([]);
    setRouteCoordinates([]);
    setRoutePlanned(false);
    toast.info("Cleared selected pins.");
    
    // Reset refs to allow map to zoom to original view
    userZoomedRef.current = false;
    initialFitRef.current = true;
    
    // Zoom out and re-center map on all lead pins
    if (mapRef.current && filteredLeads && filteredLeads.length > 0) {
      const validLeads = filteredLeads.filter(
        (l) => (l.latitude || l.lat) && (l.longitude || l.lng)
      );

      if (validLeads.length > 0) {
        const bounds = L.latLngBounds(
          validLeads.map((l) => [
            l.latitude || l.lat,
            l.longitude || l.lng,
          ])
        );

        if (bounds.isValid()) {
          mapRef.current.fitBounds(bounds, { padding: [50, 50] });
        }
      }
    }
  }, [filteredLeads]);

  const handleBoxSelect = useCallback((bounds) => {
    if (!bounds || !filteredLeads.length) return;

    const selectedInBox = filteredLeads.filter((lead) => {
      // Locked leads are already filtered out of filteredLeads via in_other_rep_route
      
      const lat = lead.latitude || lead.lat;
      const lng = lead.longitude || lead.lng;
      if (lat == null || lng == null) return false;
      return bounds.contains([lat, lng]);
    });

    if (selectedInBox.length > 0) {
      setSelectedLeads((prevSelected) => {
        const existingIds = new Set(prevSelected.map((l) => l.id));
        const newLeads = selectedInBox.filter((l) => !existingIds.has(l.id));
        return [...prevSelected, ...newLeads];
      });
      toast.success(`✅ Added ${selectedInBox.length} lead(s) to route`);
    } else {
      toast.info("No available leads found in selection area");
    }
  }, [filteredLeads]);

  const handleBoxSelectStart = useCallback((e) => {
    if (!boxSelectMode) return;
    const map = e.target;
    const latlng = map.mouseEventToLatLng(e.originalEvent);
    setBoxStart(latlng);
    setIsDrawingBox(true);
  }, [boxSelectMode]);

  const handleBoxSelectMove = useCallback((e) => {
    if (!boxSelectMode || !isDrawingBox || !boxStart) return;
    const map = e.target;
    const latlng = map.mouseEventToLatLng(e.originalEvent);
    const bounds = L.latLngBounds([boxStart, latlng]);
    setSelectionBox(bounds);
  }, [boxSelectMode, isDrawingBox, boxStart]);

  // Sold date filter handlers
  const handleGeocodeMissing = async () => {
    setGeocodeMissingLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("limit", "30");
      if (selectedTerritories.length === 1) params.set("territory", selectedTerritories[0]);
      const res = await API.post(`leads/geocode-missing/?${params.toString()}`, null, {
        timeout: 120000, // 2 minutes for up to 30 geocode calls + delays
      });
      const { geocoded, failed, remaining } = res.data;
      if (geocoded > 0) {
        tSuccess(`Geocoded ${geocoded} lead(s). ${remaining > 0 ? `${remaining} still missing – run again to continue.` : "All done."}`);
        if (viewportFetchEnabled) {
          setViewportRefreshTick((t) => t + 1);
        } else {
          fetchLeads();
        }
      } else if (remaining === 0) {
        tSuccess("No leads missing coordinates.");
      } else {
        toast.warning(`No coordinates found for this batch (${failed} failed). ${remaining} remaining.`);
      }
    } catch (err) {
      console.error("Geocode missing error:", err);
      tError(err.response?.data?.detail || "Failed to geocode missing leads.");
    } finally {
      setGeocodeMissingLoading(false);
    }
  };

  const handleSoldDateFilterClick = (event) => {
    setSoldDateMenuAnchor(event.currentTarget);
  };

  const handleSoldDateFilterClose = () => {
    setSoldDateMenuAnchor(null);
  };

  const handleSoldDateFilterSelect = (option) => {
    if (option === 'custom') {
      setSoldDateFilter('custom');
      setSoldDateButtonAnchor(soldDateButtonRef.current);
    } else if (option === 'clear') {
      setSoldDateFilter(null);
      setSoldDateRange({ start: null, end: null });
      setSoldDateButtonAnchor(null);
    } else if (typeof option === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(option)) {
      setSoldDateFilter(option);
      setSoldDateRange({ start: null, end: null });
      setSoldDateButtonAnchor(null);
    }
    handleSoldDateFilterClose();
  };

  const formatLeadDateLabel = (yyyyMmDd) => {
    if (!yyyyMmDd) return '';
    const d = new Date(yyyyMmDd + 'T12:00:00');
    return isNaN(d.getTime()) ? yyyyMmDd : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const handleCloseDateRangePopover = () => {
    setSoldDateButtonAnchor(null);
  };

  const handleBoxSelectEnd = useCallback((e) => {
    if (!boxSelectMode || !isDrawingBox || !boxStart) {
      setIsDrawingBox(false);
      setBoxStart(null);
      setSelectionBox(null);
      return;
    }
    const map = e.target;
    const latlng = map.mouseEventToLatLng(e.originalEvent);
    const bounds = L.latLngBounds([boxStart, latlng]);
    
    handleBoxSelect(bounds);

    setBoxSelectMode(false);
    setIsDrawingBox(false);
    setSelectionBox(null);
    setBoxStart(null);

    toast.info("Box select turned off");
  }, [boxSelectMode, isDrawingBox, boxStart, handleBoxSelect]);

  const handleStatusFilterToggle = useCallback((statusKey) => {
    setSelectedStatusFilters(prev => {
      const newSet = new Set(prev);
      if (newSet.has(statusKey)) {
        newSet.delete(statusKey);
      } else {
        newSet.add(statusKey);
      }
      return newSet;
    });
  }, []);

  const isStatusSelected = useCallback((statusKey) => {
    return selectedStatusFilters.has(statusKey);
  }, [selectedStatusFilters]);

  const handleMobileClearAll = useCallback(() => {
    setSelectedStatusFilters(new Set());
    setSoldDateFilter(null);
    setSoldDateRange({ start: null, end: null });
    setLegendOpen(false);
    userZoomedRef.current = false;
    initialFitRef.current = true;
    
    if (mapRef.current && filteredLeads && filteredLeads.length > 0) {
      const validLeads = filteredLeads.filter(
        (l) => (l.latitude || l.lat) && (l.longitude || l.lng)
      );

      if (validLeads.length > 0) {
        const bounds = L.latLngBounds(
          validLeads.map((l) => [
            l.latitude || l.lat,
            l.longitude || l.lng,
          ])
        );

        if (bounds.isValid()) {
          mapRef.current.fitBounds(bounds, { padding: [50, 50] });
        }
      }
    }
  }, [filteredLeads]);

  // ========== COMPUTED VALUES ==========
  const activeDesktopFilterCount =
    (selectedStatusFilters?.size || 0) +
    (soldDateFilter ? 1 : 0) +
    (selectedTerritories?.length > 0 ? 1 : 0);

  const canSaveRoute = selectedLeads.length >= 2;

  const persistLeadsMapLastView = user?.id != null;

  // ========== BOX SELECTION COMPONENTS ==========
  function BoxSelectHandler({ enabled, onStart, onMove, onEnd }) {
    const map = useMap();
    
    useEffect(() => {
      if (!enabled) {
        map.dragging.enable();
        return;
      }

      map.dragging.disable();

      const container = map.getContainer();
      
      const handleMouseDown = (e) => {
        if (e.button !== 0) return;
        e.preventDefault();
        e.stopPropagation();
        onStart({ target: map, originalEvent: e });
      };

      const handleMouseMove = (e) => {
        if (enabled) {
          e.preventDefault();
          e.stopPropagation();
        }
        onMove({ target: map, originalEvent: e });
      };

      const handleMouseUp = (e) => {
        if (enabled) {
          e.preventDefault();
          e.stopPropagation();
        }
        onEnd({ target: map, originalEvent: e });
      };

      container.addEventListener('mousedown', handleMouseDown, { passive: false });
      container.addEventListener('mousemove', handleMouseMove, { passive: false });
      container.addEventListener('mouseup', handleMouseUp, { passive: false });

      return () => {
        map.dragging.enable();
        container.removeEventListener('mousedown', handleMouseDown);
        container.removeEventListener('mousemove', handleMouseMove);
        container.removeEventListener('mouseup', handleMouseUp);
      };
    }, [enabled, map, onStart, onMove, onEnd]);

    return null;
  }

  function SelectionBoxOverlay({ bounds }) {
    const map = useMap();
    const [rect, setRect] = useState(null);

    useEffect(() => {
      if (!bounds) {
        setRect(null);
        return;
      }

      const updateRect = () => {
        const sw = map.latLngToContainerPoint(bounds.getSouthWest());
        const ne = map.latLngToContainerPoint(bounds.getNorthEast());
        
        setRect({
          left: Math.min(sw.x, ne.x),
          top: Math.min(sw.y, ne.y),
          width: Math.abs(ne.x - sw.x),
          height: Math.abs(ne.y - sw.y),
        });
      };

      updateRect();
      map.on('move', updateRect);
      map.on('zoom', updateRect);

      return () => {
        map.off('move', updateRect);
        map.off('zoom', updateRect);
      };
    }, [bounds, map]);

    if (!rect) return null;

    return (
      <Box
        sx={{
          position: 'absolute',
          left: rect.left,
          top: rect.top,
          width: rect.width,
          height: rect.height,
          border: '2px dashed #0057A4',
          backgroundColor: 'rgba(0, 87, 164, 0.1)',
          pointerEvents: 'none',
          zIndex: 1000,
        }}
      />
    );
  }

  // ========== RENDER ==========

// ========== MOBILE OVERRIDE ==========
if (isMobile) {
  return (
    <LeadsMobileMapView
      filteredLeads={filteredLeads}
      selectedLeads={selectedLeads}
      viewLeadZoom={viewLeadZoom}
      setSelectedLeads={setSelectedLeads}
      handleMarkerClick={handleMarkerClick}
      legendOpen={legendOpen}
      setLegendOpen={setLegendOpen}
      mapRef={mapRef}
      userZoomedRef={userZoomedRef}
      initialFitRef={initialFitRef}
      fitModeRef={fitModeRef}
      selectedStatusFilters={selectedStatusFilters}
      isStatusSelected={isStatusSelected}
      handleStatusFilterToggle={handleStatusFilterToggle}
      handleMobileClearAll={handleMobileClearAll}
      toggleBoxSelect={toggleBoxSelect}
      boxSelectMode={boxSelectMode}
      selectionBox={selectionBox}
      handleBoxSelectStart={handleBoxSelectStart}
      handleBoxSelectMove={handleBoxSelectMove}
      handleBoxSelectEnd={handleBoxSelectEnd}
      handleClearRoute={handleClearRoute}
      handleSaveRouteClick={handleSaveRouteClick}
      showRouteBuilder={showRouteBuilder}
      setShowRouteBuilder={setShowRouteBuilder}
      selectedLeadForDrawer={selectedLeadForDrawer}
      setSelectedLeadForDrawer={setSelectedLeadForDrawer}
      handleRemoveFromRoute={handleRemoveFromRoute}
      routeCoordinates={routeCoordinates}
      showSaveModal={showSaveModal}
      setShowSaveModal={setShowSaveModal}
      token={token}
      canSaveRoute={canSaveRoute}
      currentUser={currentUser}
      leads={leads}
      territories={territories}
      selectedTerritories={selectedTerritories}
      setSelectedTerritories={setSelectedTerritories}
      role={role}
      canViewAdditionalNotes={canViewAdditionalNotes}
      soldDateFilter={soldDateFilter}
      setSoldDateFilter={setSoldDateFilter}
      soldDateRange={soldDateRange}
      setSoldDateRange={setSoldDateRange}
      lastThreeUploadDates={lastThreeUploadDates}
      formatLeadDateLabel={formatLeadDateLabel}
      viewportFetchEnabled={viewportFetchEnabled}
      territoryIdForViewport={territoryIdForViewport}
      viewportRefreshTick={viewportRefreshTick}
      applyMapData={applyMapData}
      setLoading={setLoading}
      initialMapCenter={leafletMapView.center}
      initialMapZoom={leafletMapView.zoom}
      mapLayoutKey={leafletMapKey}
      persistLeadsMapLastView={persistLeadsMapLastView}
      persistLeadsMapUserId={user?.id}
      mobileClusterEnabled={mobileClusterFeatureEnabled}
      loading={loading}
      companyHqView={companyHqView}
      restoredLastViewRef={restoredLastViewRef}
      didFitLeadsRef={didFitLeadsRef}
      ignoreNextAutoFitRef={ignoreNextAutoFitRef}
      startupGpsResolved={startupGpsResolved}
    />
  );
}

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ maxWidth: 1440, mx: "auto", width: "100%" }}>
        {/* Header Card */}
        <Card sx={{ p: 3, mb: 3, borderRadius: 3 }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ sm: "center" }}
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor:
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.08)"
                      : "rgba(25,118,210,0.08)",
                }}
              >
                <MapsHomeWorkIcon color="primary" />
              </Box>

              <Box>
                <Typography variant="h6" fontWeight={700}>
                  Lead Management
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  View leads info and assign your leads directly from the map to build your routes.
                </Typography>
              </Box>
            </Stack>

            {isSmallScreen ? (
              <Button
                variant="contained"
                size="small"
                onClick={() => navigate("/leadmap/savedroutes")}
                sx={{
                  textTransform: "none",
                  borderRadius: 2,
                  px: 2,
                  py: 0.5,
                  fontWeight: 600,
                  bgcolor: "primary.main",
                  "&:hover": { bgcolor: "primary.dark" },
                }}
              >
                📍 My Saved Routes
              </Button>
            ) : (
              <Box display="flex" alignItems="center" gap={2}>
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: "0.95rem",
                    color: theme.palette.mode === "dark" ? "rgba(255,255,255,0.85)" : "rgba(30,30,30,0.9)",
                  }}
                >
                  Leads: {filteredLeads.length}
                </Typography>
              </Box>
            )}
          </Stack>
        </Card>
      {/* --------------------------------------------------------------------Main Map Area */}
      <Box
        sx={{
          bgcolor: "action.hover",
          borderRadius: 2,
          boxShadow: 2,
          height: { xs: "calc(90vh - 150px)", md: "60vh" },
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
        }}
      >
        {loading && !viewportFetchEnabled ? (
          <Box sx={{ width: "100%", height: "100%", p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
            <Skeleton variant="rectangular" sx={{ flex: 1, minHeight: 200, borderRadius: 2 }} />
            <Box sx={{ display: "flex", gap: 1 }}>
              <Skeleton variant="rectangular" width={120} height={36} sx={{ borderRadius: 1 }} />
              <Skeleton variant="rectangular" width={100} height={36} sx={{ borderRadius: 1 }} />
            </Box>
          </Box>
        ) : leads.length === 0 && !viewportFetchEnabled ? (
          <Box sx={{ textAlign: "center", p: 3 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No leads yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Add your first lead to see them on the map.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate("/territory/add/lead")}
            >
              Add your first lead
            </Button>
          </Box>
        ) : (
          <Box
            sx={{
              width: "100%",
              height: isMobile ? (isLandscape ? "calc(100dvh - 120px)" : "calc(100vh - 200px)") : "100%",
              display: "flex",
              flexDirection: "column",
              position: "relative",
              overflow: { xs: "visible", md: "visible" },
              overflowY: { xs: "visible", md: "visible" },
              overflowX: "hidden",
              minHeight: 0,
              pb: { xs: isMobile ? "25px" : 0, md: 0 },
            }}
          >
            {/* ------------------------------------------------------------DESKTOP MAP */}
            {!isMobile && (
              <Box
data-tour="leads-map-container"
                sx={{
                  width: "100%",
                  height: "100%",
                  position: "relative",
                  borderRadius: 2,
                  overflow: "hidden",
                  flexShrink: 0,
                }}
              >
                <MapContainer
                  key={`leads-desktop-map-${leafletMapKey}-${desktopClusterActive ? "cluster" : "plain"}`}
                  center={leafletMapView.center}
                  zoom={leafletMapView.zoom}
                  whenReady={(e) => {
                    const map = e.target;
                    mapRef.current = map;
                    map.on("zoomstart", (ev) => {
                      if (ev.originalEvent) {
                        userZoomedRef.current = true;
                      }
                    });
                  }}
                  dragging={!boxSelectMode}
                  style={{ height: "100%", width: "100%", position: "relative" }}
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />

                  {viewportFetchEnabled && (
                    <MapViewportFetcher
                      territoryId={territoryIdForViewport}
                      refreshTick={viewportRefreshTick}
                      onApply={applyMapData}
                      setLoading={setLoading}
                    />
                  )}

                  <MapLastViewPersistence
                    enabled={persistLeadsMapLastView}
                    userId={user?.id}
                  />

                  <FitBoundsOnData
                    leads={filteredLeads}
                    userZoomedRef={userZoomedRef}
                    initialFitRef={initialFitRef}
                    fitModeRef={fitModeRef}
                    didFitLeadsRef={didFitLeadsRef}
                  />
                  <MapHqFallback
                    loading={loading}
                    leads={filteredLeads}
                    companyHqView={companyHqView}
                    restoredLastViewRef={restoredLastViewRef}
                    didFitLeadsRef={didFitLeadsRef}
                    userZoomedRef={userZoomedRef}
                    initialFitRef={initialFitRef}
                    gpsResolved={startupGpsResolved}
                  />
                  <CenterOnSelectedLead
                    selectedLeads={selectedLeads}
                    userZoomedRef={userZoomedRef}
                  />
                  <MapClickHandler
                    onMapClick={() => {}}
                  />

                  {desktopUserLocation && (
                    <UserLocationMapLayers
                      position={desktopUserLocation}
                      minZoom={USER_LOCATION_MIN_ZOOM_DESKTOP}
                      icon={DESKTOP_USER_LOCATION_ICON}
                      pulsingIcon={DESKTOP_USER_LOCATION_ICON_PULSE}
                      pulseZoomMin={USER_LOCATION_PULSE_ZOOM_MIN}
                      pulseZoomMax={USER_LOCATION_PULSE_ZOOM_MAX}
                      accuracyRadius={Math.max(
                        20,
                        Math.min(
                          Number.isFinite(desktopUserLocation.accuracy)
                            ? desktopUserLocation.accuracy
                            : 60,
                          200
                        )
                      )}
                      accuracyPathOptions={USER_LOCATION_ACCURACY_PATH_OPTIONS}
                      markerInteractive={desktopLocationApproximate}
                      markerChildren={
                        desktopLocationApproximate ? (
                          <LeafletTooltip direction="top" offset={[0, -12]} opacity={0.95}>
                            Approximate location
                          </LeafletTooltip>
                        ) : null
                      }
                    />
                  )}

                  {desktopClusterActive ? (
                    <ClusteredLeadMarkers
                      leads={filteredLeads}
                      selectedLeads={selectedLeads}
                      handleMarkerClick={handleMarkerClick}
                    />
                  ) : (
                    filteredLeads
                      .filter((lead) => {
                        const lat = lead.latitude || lead.lat;
                        const lng = lead.longitude || lead.lng;
                        return lat != null && lng != null && !isNaN(lat) && !isNaN(lng);
                      })
                      .map((lead) => {
                        const color = getPinColor(lead.status, lead.notes);
                        const isSelected = selectedLeads.some((l) => l.id === lead.id);
                        const isDisabled =
                          lead.in_other_rep_route;

                        const icon = isDisabled
                          ? getCachedMarkerIcon("disabled", color)
                          : isSelected
                          ? getCachedMarkerIcon("selected", color)
                          : getCachedMarkerIcon("normal", color);

                        return (
                          <Marker
                            key={lead.id}
                            position={[lead.latitude || lead.lat, lead.longitude || lead.lng]}
                            icon={icon}
                            eventHandlers={{
                              click: () => handleMarkerClick(lead),
                            }}
                            opacity={isDisabled ? 0.5 : 1}
                          />
                        );
                      })
                  )}

                  <BoxSelectHandler
                    enabled={boxSelectMode}
                    onStart={handleBoxSelectStart}
                    onMove={handleBoxSelectMove}
                    onEnd={handleBoxSelectEnd}
                  />

                {selectionBox && <SelectionBoxOverlay bounds={selectionBox} />}
                </MapContainer>

                {/* ================= DESKTOP MAP FILTERS BUTTON ================= */}
                <Box
                  sx={{
                    position: "absolute",
                    top: 16,
                    right: 16,
                    zIndex: 1000,
                  }}
                >
                  <Button
                    data-tour="leads-map-filter-button"
                    variant="contained"
                    startIcon={<FilterAltOutlinedIcon />}
                    onClick={(e) => {
                      setDesktopFiltersAnchorEl(e.currentTarget);
                      setDesktopFiltersOpen(true);
                      setShowDesktopCustomDateFields(soldDateFilter === "custom");
                    }}
                    sx={desktopFilterButtonSx}
                  >
                    {activeDesktopFilterCount > 0
                      ? `Filters (${activeDesktopFilterCount})`
                      : "Filters"}
                  </Button>
                </Box>

                {/* ================= DESKTOP MAP FILTERS PANEL ================= */}
                <Popover
                  open={desktopFiltersOpen}
                  anchorEl={desktopFiltersAnchorEl}
                  onClose={() => {
                    setDesktopFiltersOpen(false);
                    setDesktopFiltersAnchorEl(null);
                  }}
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                  marginThreshold={8}
                  disableScrollLock
                  slotProps={{
                    paper: {
                      elevation: 0,
                      sx: {
                        mt: 1,
                        width: { xs: "min(420px, calc(100vw - 24px))", sm: 420 },
                        maxHeight: "min(72vh, calc(100vh - 96px))",
                        display: "flex",
                        flexDirection: "column",
                        overflow: "hidden",
                        borderRadius: "24px",
                        bgcolor:
                          theme.palette.mode === "dark"
                            ? "rgba(15,23,42,0.98)"
                            : "rgba(255,255,255,0.98)",
                        backdropFilter: "blur(14px)",
                        WebkitBackdropFilter: "blur(14px)",
                        boxShadow:
                          theme.palette.mode === "dark"
                            ? "0 18px 40px rgba(0,0,0,0.30)"
                            : "0 18px 40px rgba(0,0,0,0.16)",
                        border: "1px solid",
                        borderColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.08)"
                            : "rgba(0,0,0,0.06)",
                      },
                    },
                  }}
                >
                      <Box
                        sx={{
                          p: 2.2,
                          pt: 1.15,
                          pb: 1.6,
                          display: "flex",
                          flexDirection: "column",
                          minHeight: 0,
                          maxHeight: "min(72vh, calc(100vh - 96px))",
                        }}
                      >
                        <Box
                          sx={{
                            width: 42,
                            height: 4,
                            borderRadius: 999,
                            bgcolor: "rgba(120,120,120,0.35)",
                            mx: "auto",
                            mb: 1.5,
                          }}
                        />

                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            justifyContent: "space-between",
                            gap: 1.5,
                            mb: 1.3,
                          }}
                        >
                          <Box>
                            <Typography sx={{ fontSize: 20, fontWeight: 700, lineHeight: 1.1 }}>
                              Filters
                            </Typography>
                            <Typography sx={{ fontSize: 13, color: "text.secondary", mt: 0.35 }}>
                              Narrow leads on the map
                            </Typography>
                          </Box>
                          <IconButton
                            size="small"
                            onClick={() => {
                              setDesktopFiltersOpen(false);
                              setDesktopFiltersAnchorEl(null);
                            }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Box>

                        <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", pr: 0.25 }}>
                        <Stack spacing={0.35}>
                          {/* Lead Status */}
                          <Button
                            fullWidth
                            variant="text"
                            onClick={() => setShowDesktopStatusSection((prev) => !prev)}
                            sx={desktopFilterRowButtonSx}
                          >
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.1, minWidth: 0 }}>
                              <TuneIcon sx={{ fontSize: 18, color: "primary.main" }} />
                              <Box sx={{ textAlign: "left", minWidth: 0 }}>
                                <Typography sx={{ fontSize: 14, fontWeight: 700, lineHeight: 1.2 }}>
                                  Lead Status
                                </Typography>
                                <Typography sx={{ fontSize: 12.5, color: "text.secondary", lineHeight: 1.25, mt: 0.2 }}>
                                  {selectedStatusFilters.size > 0
                                    ? `${selectedStatusFilters.size} active filter${selectedStatusFilters.size > 1 ? "s" : ""}`
                                    : "No status filters applied"}
                                </Typography>
                              </Box>
                            </Box>
                            {selectedStatusFilters.size > 0 && (
                              <Chip
                                size="small"
                                label={selectedStatusFilters.size}
                                sx={{
                                  height: 22,
                                  fontSize: "0.74rem",
                                  fontWeight: 700,
                                  borderRadius: "999px",
                                  bgcolor: "primary.main",
                                  color: "#fff",
                                }}
                              />
                            )}
                          </Button>

                          {showDesktopStatusSection && (
                            <Box sx={{ mt: 0.35, p: 1.35, borderRadius: "16px", ...glassyPanelSx }}>
                              {selectedStatusFilters.size > 0 && (
                                <Box sx={{ mb: 1, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                  <Typography variant="caption" sx={{ fontSize: 11, fontWeight: 600 }}>
                                    {selectedStatusFilters.size} filter(s) active
                                  </Typography>
                                  <Button
                                    size="small"
                                    variant="text"
                                    onClick={() => setSelectedStatusFilters(new Set())}
                                    sx={{ minWidth: "auto", px: 1, py: 0.25, fontSize: 10, textTransform: "none", color: "primary.main" }}
                                  >
                                    Clear All
                                  </Button>
                                </Box>
                              )}
                              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0.6 }}>
                                {Object.entries(STATUS_COLORS).map(([label, color]) => {
                                  const statusKey = label.toLowerCase();
                                  const isSelected = isStatusSelected(statusKey);
                                  return (
                                    <Box
                                      key={label}
                                      onClick={() => handleStatusFilterToggle(statusKey)}
                                      sx={{
                                        display: "flex", alignItems: "center", gap: 1,
                                        cursor: "pointer", px: 0.25, py: 0.15, borderRadius: 1,
                                        bgcolor: isSelected ? "action.selected" : "transparent",
                                        "&:hover": { bgcolor: "action.hover" },
                                        transition: "background-color 0.2s",
                                      }}
                                    >
                                      <Box sx={{
                                        width: 12, height: 12, borderRadius: "50%", bgcolor: color,
                                        border: isSelected ? "2px solid" : "none",
                                        borderColor: isSelected ? "primary.main" : "transparent",
                                        boxShadow: isSelected ? "0 0 0 2px rgba(25,118,210,0.2)" : "none",
                                      }} />
                                      <Typography sx={{ fontSize: "13px" }}>{label}</Typography>
                                    </Box>
                                  );
                                })}
                                {[
                                  ["new-adt", "#F5D327", "New (ADT)"],
                                  ["new-title-realtor", "#A627F5", "New (Title Co)"],
                                  ["new-builder", "#F527C8", "New (Builder)"],
                                ].map(([key, color, label]) => {
                                  const isSelected = isStatusSelected(key);
                                  return (
                                    <Box
                                      key={key}
                                      onClick={() => handleStatusFilterToggle(key)}
                                      sx={{
                                        display: "flex", alignItems: "center", gap: 1,
                                        cursor: "pointer", px: 0.25, py: 0.15, borderRadius: 1,
                                        bgcolor: isSelected ? "action.selected" : "transparent",
                                        "&:hover": { bgcolor: "action.hover" },
                                        transition: "background-color 0.2s",
                                      }}
                                    >
                                      <Box sx={{
                                        width: 12, height: 12, borderRadius: "50%", bgcolor: color,
                                        border: isSelected ? "2px solid" : "none",
                                        borderColor: isSelected ? "primary.main" : "transparent",
                                        boxShadow: isSelected ? "0 0 0 2px rgba(25,118,210,0.2)" : "none",
                                      }} />
                                      <Typography sx={{ fontSize: "13px" }}>{label}</Typography>
                                    </Box>
                                  );
                                })}
                              </Box>
                            </Box>
                          )}

                          {/* Lead Date */}
                          <Button
                            fullWidth
                            variant="text"
                            onClick={() => setShowDesktopDateSection((prev) => !prev)}
                            sx={desktopFilterRowButtonSx}
                          >
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.1, minWidth: 0 }}>
                              <CalendarTodayIcon sx={{ fontSize: 18, color: "primary.main" }} />
                              <Box sx={{ textAlign: "left", minWidth: 0 }}>
                                <Typography sx={{ fontSize: 14, fontWeight: 700, lineHeight: 1.2 }}>
                                  Lead Date
                                </Typography>
                                <Typography sx={{ fontSize: 12.5, color: "text.secondary", lineHeight: 1.25, mt: 0.2 }}>
                                  {soldDateFilter
                                    ? soldDateFilter === "custom"
                                      ? "Custom date range"
                                      : formatLeadDateLabel(soldDateFilter)
                                    : "No date filter applied"}
                                </Typography>
                              </Box>
                            </Box>
                          </Button>

                          {showDesktopDateSection && (
                            <Box sx={{ mt: 0.35, p: 1.35, borderRadius: "16px", ...glassyPanelSx }}>
                              <Stack spacing={0.7}>
                                {lastThreeUploadDates.map((dateStr) => (
                                  <Button
                                    key={`desktop-lead-date-${dateStr}`}
                                    fullWidth
                                    variant={soldDateFilter === dateStr ? "contained" : "text"}
                                    onClick={() => {
                                      setSoldDateFilter(dateStr);
                                      setSoldDateRange({ start: null, end: null });
                                      setShowDesktopCustomDateFields(false);
                                    }}
                                    sx={{ justifyContent: "flex-start", textTransform: "none", borderRadius: "12px" }}
                                  >
                                    {formatLeadDateLabel(dateStr)}
                                  </Button>
                                ))}
                                <Button
                                  fullWidth
                                  variant={soldDateFilter === "custom" ? "contained" : "text"}
                                  onClick={() => {
                                    setSoldDateFilter("custom");
                                    setShowDesktopCustomDateFields((prev) => !prev || soldDateFilter !== "custom");
                                  }}
                                  sx={{ justifyContent: "flex-start", textTransform: "none", borderRadius: "12px" }}
                                >
                                  Custom Date Range
                                </Button>
                                {showDesktopCustomDateFields && (
                                  <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
                                    <TextField
                                      type="date"
                                      label="Start Date"
                                      size="small"
                                      fullWidth
                                      value={soldDateRange.start || ""}
                                      onChange={(e) => setSoldDateRange((prev) => ({ ...prev, start: e.target.value }))}
                                      InputLabelProps={{ shrink: true }}
                                      sx={compactDateFieldSx}
                                    />
                                    <TextField
                                      type="date"
                                      label="End Date"
                                      size="small"
                                      fullWidth
                                      value={soldDateRange.end || ""}
                                      onChange={(e) => setSoldDateRange((prev) => ({ ...prev, end: e.target.value }))}
                                      InputLabelProps={{ shrink: true }}
                                      sx={compactDateFieldSx}
                                    />
                                  </Box>
                                )}
                              </Stack>
                            </Box>
                          )}

                          {/* Territories */}
                          {["Admin", "Sales Director", "Manager"].includes(role) && (
                            <>
                              <Button
                                fullWidth
                                variant="text"
                                onClick={() => setShowDesktopTerritorySection((prev) => !prev)}
                                sx={desktopFilterRowButtonSx}
                              >
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1.1, minWidth: 0 }}>
                                  <MapIcon sx={{ fontSize: 18, color: "primary.main" }} />
                                  <Box sx={{ textAlign: "left", minWidth: 0 }}>
                                    <Typography sx={{ fontSize: 14, fontWeight: 700, lineHeight: 1.2 }}>
                                      Territories
                                    </Typography>
                                    <Typography sx={{ fontSize: 12.5, color: "text.secondary", lineHeight: 1.25, mt: 0.2 }}>
                                      {selectedTerritories.length > 0
                                        ? `${selectedTerritories.length} territor${selectedTerritories.length === 1 ? "y" : "ies"} selected`
                                        : "No territory filter applied"}
                                    </Typography>
                                  </Box>
                                </Box>
                                {selectedTerritories.length > 0 && (
                                  <Chip
                                    size="small"
                                    label={selectedTerritories.length}
                                    sx={{
                                      height: 22, fontSize: "0.74rem", fontWeight: 700,
                                      borderRadius: "999px", bgcolor: "primary.main", color: "#fff",
                                    }}
                                  />
                                )}
                              </Button>

                              {showDesktopTerritorySection && (
                                <Box sx={{ mt: 0.35, p: 1.35, borderRadius: "16px", ...glassyPanelSx, maxHeight: 220, overflowY: "auto" }}>
                                  {selectedTerritories.length > 0 && (
                                    <Box sx={{ mb: 1, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                      <Typography variant="caption" sx={{ fontSize: 11, fontWeight: 600 }}>
                                        {selectedTerritories.length} territory(ies) selected
                                      </Typography>
                                      <Button
                                        size="small"
                                        variant="text"
                                        onClick={() => {
                                          setSelectedTerritories([]);
                                          userZoomedRef.current = false;
                                          initialFitRef.current = true;
                                          fitModeRef.current = "global";
                                        }}
                                        sx={{ minWidth: "auto", px: 1, py: 0.25, fontSize: 10, textTransform: "none", color: "primary.main" }}
                                      >
                                        Clear All
                                      </Button>
                                    </Box>
                                  )}
                                  {territories.length === 0 ? (
                                    <Typography variant="body2" sx={{ fontSize: 12, color: "text.secondary", py: 1 }}>
                                      No territories available
                                    </Typography>
                                  ) : (
                                    territories.map((territory) => {
                                      const isSelected = selectedTerritories.includes(territory.id);
                                      return (
                                        <Box
                                          key={territory.id}
                                          onClick={() => {
                                            setSelectedTerritories((prev) =>
                                              isSelected
                                                ? prev.filter((id) => id !== territory.id)
                                                : [...prev, territory.id]
                                            );
                                          }}
                                          sx={{
                                            display: "flex", alignItems: "center", gap: 1,
                                            cursor: "pointer", px: 1, py: 0.75, borderRadius: 1,
                                            bgcolor: isSelected ? "action.selected" : "transparent",
                                            "&:hover": { bgcolor: "action.hover" },
                                            transition: "background-color 0.2s",
                                            mb: 0.5,
                                          }}
                                        >
                                          <Box sx={{
                                            width: 12, height: 12, borderRadius: "2px",
                                            bgcolor: isSelected ? "primary.main" : "grey.300",
                                            border: isSelected ? "2px solid" : "1px solid",
                                            borderColor: isSelected ? "primary.dark" : "grey.400",
                                          }} />
                                          <Typography sx={{ fontSize: "13px" }}>{territory.name}</Typography>
                                        </Box>
                                      );
                                    })
                                  )}
                                </Box>
                              )}
                            </>
                          )}
                        </Stack>
                        </Box>

                        <Divider sx={{ my: 1.4 }} />

                        <Stack direction="row" spacing={1}>
                          <Button
                            fullWidth
                            variant="outlined"
                            onClick={() => {
                              setSelectedStatusFilters(new Set());
                              setSoldDateFilter(null);
                              setSoldDateRange({ start: null, end: null });
                              setSelectedTerritories([]);
                              setShowDesktopCustomDateFields(false);
                              userZoomedRef.current = false;
                              initialFitRef.current = true;
                              fitModeRef.current = "global";
                            }}
                            sx={{ borderRadius: "14px", py: 1, textTransform: "none", fontWeight: 700 }}
                          >
                            Clear All
                          </Button>
                          <Button
                            fullWidth
                            variant="contained"
                            onClick={() => {
                              setDesktopFiltersOpen(false);
                              setDesktopFiltersAnchorEl(null);
                            }}
                            sx={{ borderRadius: "14px", py: 1, textTransform: "none", fontWeight: 700 }}
                          >
                            Apply Filters
                          </Button>
                        </Stack>
                      </Box>
                </Popover>

                {/* ================= DESKTOP GEOCODE FAB ================= */}
                <Box sx={desktopGeoFabSx}>
                  <Button
                    data-tour="leads-map-geocode-button"
                    variant="text"
                    disabled={geocodeMissingLoading}
                    onClick={handleGeocodeMissing}
                    startIcon={
                      geocodeMissingLoading ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        <NavigationIcon sx={{ fontSize: 18 }} />
                      )
                    }
                    sx={{
                      textTransform: "none",
                      fontWeight: 700,
                      color: "inherit",
                      minWidth: 0,
                      px: 0,
                      py: 0,
                      "&:hover": {
                        backgroundColor: "transparent",
                      },
                    }}
                  >
                    {geocodeMissingLoading ? "Geocoding…" : "Geocode Missing Leads"}
                  </Button>
                </Box>

                {/* ================= DESKTOP MY LOCATION ================= */}
                {desktopCanLocate && (
                  <>
                    <MuiTooltip title="Center map on my location">
                      <Box
                        component="span"
                        sx={{
                          position: "absolute",
                          left: 12,
                          top: 96,
                          zIndex: 2000,
                          display: "inline-flex",
                          lineHeight: 0,
                        }}
                      >
                        <IconButton
                          aria-label="Center map on my location"
                          disabled={desktopLocating}
                          onClick={() => handleDesktopLocate(false)}
                          onDoubleClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                          sx={desktopMyLocationIconButtonSx}
                        >
                          {desktopLocating ? (
                            <CircularProgress size={18} color="inherit" />
                          ) : (
                            <MyLocationIcon sx={{ fontSize: 20 }} />
                          )}
                        </IconButton>
                      </Box>
                    </MuiTooltip>
                  </>
                )}
              </Box>
            )}
          </Box>
        )}
      </Box>
            

           

      {/* ------------------------------------------------------------------------DESKTOP: Route Builder Table - Outside map container */}
      {!isMobile && (
        <Paper
	data-tour="route-builder-table"
          elevation={1}
          sx={{
            width: "100%",
            maxWidth: "100%",
            margin: "0 auto",
            p: 2,
            mt: 0.5,
            mb: 2,
            display: "flex",
            flexDirection: "column",
            maxHeight: "500px",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1,
              flexShrink: 0,
            }}
          >
            <Typography variant="subtitle1">
              Route Builder • Total Stops: <strong>{selectedLeads.length}</strong>
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                size="small"
                variant="outlined"
                color="inherit"
                startIcon={<ClearIcon />}
                onClick={handleClearRoute}
                disabled={selectedLeads.length === 0}
              >
                Clear All
              </Button>
              {canSaveRoute && (
                <Button
                  size="small"
                  variant="contained"
                  color="success"
                  startIcon={<SaveIcon />}
                  onClick={() => {
                    setShowSaveModal(true);
                  }}
                >
                  Save Route
                </Button>
              )}
              <Button
		data-tour="saved-routes-button"
                size="small"
                variant="outlined"
                color="primary"
                onClick={() => navigate("/leadmap/savedroutes")}
              >
                Saved Routes
              </Button>
              <Button
                data-tour="leads-map-box-select-button"
                size="small"
                variant={boxSelectMode ? "contained" : "outlined"}
                color="primary"
                startIcon={<CropFreeIcon />}
                onClick={() => {
                  setBoxSelectMode(!boxSelectMode);
                  if (boxSelectMode) {
                    setSelectionBox(null);
                    setIsDrawingBox(false);
                    setBoxStart(null);
                  }
                }}
              >
                {boxSelectMode ? "Exit Box Select" : "Box Select"}
              </Button>
            </Box>
          </Box>
          {boxSelectMode && (
            <Box data-tour="leads-map-box-select-hint" sx={{ mb: 1, flexShrink: 0 }}>
              <Typography variant="caption" color="text.secondary">
                Click and drag on the map to select multiple leads
              </Typography>
            </Box>
          )}

          <Divider sx={{ mb: 2, flexShrink: 0 }} />

          <Box
            sx={{
              overflowY: "auto",
              overflowX: "hidden",
              flex: 1,
              minHeight: 0,
            }}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell width={60} sx={{ fontWeight: 700 }}>
                    #
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Lead</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Address</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Notes</TableCell>
                  <TableCell align="right" width={120} sx={{ fontWeight: 700 }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedLeads.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      Select pins on the map to add them to this route.
                    </TableCell>
                  </TableRow>
                ) : (
                  selectedLeads.map((lead, idx) => (
                    <TableRow key={lead.id} hover>
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell>
                        {lead.first_name} {lead.last_name}
                      </TableCell>
                      <TableCell>
                        {lead.address
                          ? `${lead.address}, ${lead.city || ""}, ${lead.state || ""} ${lead.zip_code || ""}`
                          : "—"}
                      </TableCell>
                      <TableCell>{lead.status || "—"}</TableCell>
                      <TableCell>{lead.notes || "—"}</TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.75} justifyContent="flex-end">
                          <Button
                            size="small"
                            variant="outlined"
                            data-tour="route-builder-view-btn"
                            onClick={() => setSelectedLeadForDrawer(lead)}
                          >
                            View
                          </Button>
                          <Button
                            size="small"
                            color="error"
                            variant="outlined"
                            onClick={() => handleRemoveFromRoute(lead.id)}
                          >
                            Remove
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Box>
        </Paper>
      )}

      <ConfirmDialog
        open={confirmDelete}
        onClose={() => {
          setConfirmDelete(false);
          setLeadToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        title="Delete Lead"
        message="Archive this lead? They will be removed from active lists and can be restored later."
        itemName={
          leadToDelete
            ? `${leadToDelete.first_name} ${leadToDelete.last_name}`
            : null
        }
      />

      <SaveRouteModal
        open={showSaveModal}
        onClose={() => setShowSaveModal(false)}
        selectedLeads={selectedLeads}
        route={routeCoordinates}
        token={token}
      />

      {/* ================= DESKTOP LEAD DETAIL DRAWER ================= */}
      {!isMobile && (
        <Drawer
          anchor="right"
          open={!!selectedLeadForDrawer}
          onClose={() => { setSelectedLeadForDrawer(null); setDesktopDrawerExpanded(false); }}
          PaperProps={{
            "data-tour": "desktop-lead-drawer",
            sx: {
              width: 400,
              height: "auto",
              maxHeight: "82vh",
              top: "50%",
              transform: "translateY(-50%) !important",
              borderRadius: "16px 0 0 16px",
              bgcolor: theme.palette.mode === "dark" ? "rgba(15,23,42,0.98)" : "rgba(255,255,255,0.99)",
              backdropFilter: "blur(14px)",
              WebkitBackdropFilter: "blur(14px)",
              boxShadow: theme.palette.mode === "dark"
                ? "-10px 0 40px rgba(0,0,0,0.55)"
                : "-10px 0 40px rgba(0,0,0,0.14)",
              borderLeft: "1px solid",
              borderColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
            },
          }}
          sx={{ zIndex: 1300 }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", maxHeight: "82vh", overflow: "hidden" }}>

            {/* Scrollable content */}
            <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", p: 2.5, pt: 2 }}>

              {/* Header */}
              <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1.5, mb: 2 }}>
                <Box>
                  <Typography sx={{ fontSize: 22, fontWeight: 700, lineHeight: 1.1, letterSpacing: "-0.2px" }}>
                    Lead Details
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: "text.secondary", mt: 0.35 }}>
                    View and update lead info
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={desktopDrawerSaving ? null : <SaveIcon sx={{ fontSize: 16 }} />}
                    disabled={desktopDrawerSaving}
                    onClick={handleDesktopDrawerSave}
                    sx={{ minWidth: "auto", px: 1.35, py: 0.7, borderRadius: 2.5, textTransform: "none", fontWeight: 700, boxShadow: "0 6px 16px rgba(26,115,232,0.24)" }}
                  >
                    {desktopDrawerSaving ? "Saving…" : "Save"}
                  </Button>
                  <IconButton size="small" onClick={() => { setSelectedLeadForDrawer(null); setDesktopDrawerExpanded(false); }}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>

              {displayLead && (
                <>
                  {/* Info card */}
                  <Card elevation={0} sx={{ p: 1.5, borderRadius: 3, border: "1px solid rgba(0,0,0,0.06)", bgcolor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.03)" : "#FAFBFC", mb: 2 }}>
                    <Typography sx={{ fontSize: 17, fontWeight: 700, lineHeight: 1.15 }}>
                      {displayLead.first_name} {displayLead.last_name}
                    </Typography>
                    <Typography sx={{ fontSize: 15, fontWeight: 600, lineHeight: 1.2, color: "text.primary", mb: 0.35 }}>
                      {displayLead.address || "No address available"}
                    </Typography>
                    <Typography sx={{ fontSize: 14, color: "text.secondary", lineHeight: 1.3, mb: 1.1 }}>
                      {[displayLead.city, displayLead.state, displayLead.zip_code].filter(Boolean).join(", ") || "—"}
                    </Typography>
                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5, mt: 0.2 }}>
                      <Box>
                        <Typography sx={desktopDrawerStatLabelSx}>Date Sold</Typography>
                        <Typography sx={desktopDrawerStatValueSx}>
                          {displayLead.sold_date ? new Date(displayLead.sold_date).toLocaleDateString() : "—"}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography sx={desktopDrawerStatLabelSx}>Price</Typography>
                        <Typography sx={desktopDrawerStatValueSx}>
                          {displayLead.price ? `$${Number(displayLead.price).toLocaleString()}` : "—"}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography sx={desktopDrawerStatLabelSx}>Square Feet</Typography>
                        <Typography sx={desktopDrawerStatValueSx}>
                          {displayLead.square_feet ? Number(displayLead.square_feet).toLocaleString() : "—"}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography sx={desktopDrawerStatLabelSx}>Status</Typography>
                        <Chip
                          label={displayLead.status || "New"}
                          size="small"
                          sx={{
                            mt: 0.35, borderRadius: 999, fontWeight: 600, fontSize: 12, height: "auto", py: 0.25,
                            bgcolor: getDesktopStatusChipStyle(displayLead.status).bg,
                            color: getDesktopStatusChipStyle(displayLead.status).color,
                            border: "none",
                            "& .MuiChip-label": { px: 1.25 },
                          }}
                        />
                      </Box>
                    </Box>
                  </Card>

                  <Box sx={{ mb: 1.5 }}>
                    <Typography sx={{ fontSize: 12, color: "text.secondary", mb: 0.35 }}>Notes</Typography>
                    <Typography sx={{ fontSize: 14, color: "text.primary" }}>{displayLead.notes || "No notes"}</Typography>
                  </Box>
                  {canViewAdditionalNotes && (
                    <Box sx={{ mb: 2 }}>
                      <Typography sx={{ fontSize: 12, color: "text.secondary", mb: 0.35 }}>Additional Notes</Typography>
                      <Typography sx={{ fontSize: 14, color: "text.primary" }}>{displayLead.additional_notes || "—"}</Typography>
                    </Box>
                  )}

                  {/* Edit fields — always visible */}
                  <Divider sx={{ my: 1.5 }} />
                  <Box>
                    <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
                      <TextField size="small" fullWidth placeholder="First name" value={desktopDrawerEditData.first_name || ""} onChange={(e) => setDesktopDrawerEditData({ ...desktopDrawerEditData, first_name: e.target.value })} />
                      <TextField size="small" fullWidth placeholder="Last name" value={desktopDrawerEditData.last_name || ""} onChange={(e) => setDesktopDrawerEditData({ ...desktopDrawerEditData, last_name: e.target.value })} />
                    </Box>
                    <TextField size="small" fullWidth type="email" placeholder="Email" value={desktopDrawerEditData.email || ""} onChange={(e) => setDesktopDrawerEditData({ ...desktopDrawerEditData, email: e.target.value })} sx={{ mb: 1 }} />
                    <TextField size="small" fullWidth type="tel" placeholder="Phone" value={desktopDrawerEditData.phone || ""} onChange={(e) => setDesktopDrawerEditData({ ...desktopDrawerEditData, phone: e.target.value })} sx={{ mb: 1 }} />
                    <FormControl fullWidth size="small" sx={{ mb: 1 }} disabled={!(desktopDrawerEditData.phone || "").trim()}>
                      <InputLabel required={(desktopDrawerEditData.phone || "").trim().length > 0}>SMS/Email Opt-In</InputLabel>
                      <Select
                        value={desktopDrawerEditData.sms_email_opt_in ?? ""}
                        label="SMS/Email Opt-In"
                        onChange={(e) => setDesktopDrawerEditData({ ...desktopDrawerEditData, sms_email_opt_in: e.target.value })}
                      >
                        <MenuItem value=""><em>—</em></MenuItem>
                        <MenuItem value="yes">Yes</MenuItem>
                        <MenuItem value="no">No</MenuItem>
                      </Select>
                      {!(desktopDrawerEditData.phone || "").trim() && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, fontSize: 11 }}>
                          Enter a phone number to set opt-in status
                        </Typography>
                      )}
                    </FormControl>
                    <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                      <Select
                        value={desktopDrawerEditData.status || "New"}
                        onChange={(e) => setDesktopDrawerEditData({ ...desktopDrawerEditData, status: e.target.value })}
                        MenuProps={{
                          slotProps: {
                            paper: { sx: { maxHeight: 280, borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.12)" } },
                          },
                        }}
                      >
                        {DESKTOP_DRAWER_STATUS_OPTIONS.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: 13, py: 1.25 }}>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <LeadStatusIcon status={opt.value} size={18} />
                                {opt.label}
                              </Box>
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                    {desktopDrawerEditData.status === "Follow Up Needed" && (
                      <TextField
                        size="small"
                        fullWidth
                        type="date"
                        label="Follow-up date"
                        InputLabelProps={{ shrink: true }}
                        value={desktopDrawerEditData.followUpDate || ""}
                        onChange={(e) =>
                          setDesktopDrawerEditData({
                            ...desktopDrawerEditData,
                            followUpDate: e.target.value,
                          })
                        }
                        sx={{ mb: 1 }}
                      />
                    )}
                    <TextField
                      size="small" fullWidth multiline minRows={3} placeholder="Notes"
                      value={desktopDrawerEditData.notes || ""}
                      onChange={(e) => setDesktopDrawerEditData({ ...desktopDrawerEditData, notes: e.target.value })}
                    />
                  </Box>
                </>
              )}
            </Box>

            {/* Sticky footer — Add to Route */}
            {displayLead && (
              <Box sx={{ flexShrink: 0, p: 2.5, pt: 1.5, borderTop: "1px solid", borderColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)", bgcolor: "background.paper" }}>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  data-tour="desktop-drawer-add-route"
                  disabled={displayLead.in_other_rep_route}
                  onClick={() => {
                    if (displayLead.in_other_rep_route) {
                      toast.info("This lead is already in another sales rep's route");
                      return;
                    }
                    const alreadySelected = selectedLeads.some((l) => l.id === displayLead.id);
                    setSelectedLeads((prev) =>
                      alreadySelected
                        ? prev.filter((l) => l.id !== displayLead.id)
                        : [...prev, displayLead]
                    );
                    toast.success(alreadySelected ? "Removed from route" : "Added to route");
                    setSelectedLeadForDrawer(null);
                  }}
                  sx={{ borderRadius: 3, py: 1.1, textTransform: "none", fontWeight: 700, boxShadow: "0 6px 16px rgba(26,115,232,0.24)" }}
                >
                  {selectedLeads.some((l) => l.id === displayLead?.id) ? "Remove from Route" : "Add to Route"}
                </Button>
              </Box>
            )}
          </Box>
        </Drawer>
      )}

      </Box>
    </Box>
  );
};

export default Leads;